import { Component, ElementRef, Input, NgZone, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { currentUser } from 'auth-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IEntity, IMergeFile, INamedEntity, IWorkflowService } from 'interfaces-lib';
import { LocalfileUploadService } from 'localfile-upload-service';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { AsyncSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { WorkflowTaskRunner } from 'workflow-task-service-lib';
import { runtimeEnvironment } from '../../environments/environment';
import { ConfirmationDialogComponent } from '../common-components/confirmation-dialog/confirmation-dialog.component';
import { ISimpleTableRow } from '../common-components/simple-table/simple-table.interfaces';
import { IFileToPathConverter } from '../interfaces/ifiletopathconverter';
import { IPreMergeChecker } from '../interfaces/ipremergechecker';
import { ILocalMergeFile, MergeControllerService, MergeStatus } from '../merge-component/merge-controller.service';
import { FileListControllerService } from '../services/file-list.controller.service';
import { MergeFilesTask } from '../tasks/merge-files-task';

export interface IMergeBasketViewConfig {
  readonly namespace: string;
  readonly workflowService: IWorkflowService;
  readonly entityType: string;
  readonly fileType: string;
  readonly fileToPathConverter: IFileToPathConverter;
  readonly preMergeChecker: IPreMergeChecker;
}

export interface IFileMergeController {
  fileMergeUpdate(entity: IEntity, status: string): void;
  fileMergeComplete(entity: IEntity): void;
  fileMergeError(entity: IEntity, message: string): void;
}

@Component({
  selector: 'app-merge-basket-view',
  templateUrl: './merge-basket-view.component.html',
  styleUrls: ['./merge-basket-view.scss', './chrome-extension.merge-basket-view.scss', './web-app.merge-basket-view.scss'],
  standalone: false,
})
export class MergeBasketViewComponent implements IFileMergeController {
  public static LOCAL_FILE = 'localFile';
  public static FILE_ATTACHMENT = 'fileAttachment';
  public static DOCUMENT = 'document';

  @Input()
  config: IMergeBasketViewConfig;

  @Input()
  filesListOpen = true;

  renderMode = runtimeEnvironment.renderMode;

  tooltipVisible = false;
  tooltipTimer: NodeJS.Timeout;

  files: ISimpleTableRow[] = [];
  totalFileSizeBytes = 0;
  readonly maxBasketFileSizeBytes = MergeBasketViewComponent.MAX_BASKET_SIZE_BYTES; // public version for HTML

  columnHeadings = [
    { title: 'Filename', width: '51%' },
    { title: 'Owner', width: '25%' },
    { title: 'Actions', width: '24%', align: 'right' },
  ];

  currentUserId: string;

  @ViewChild('inputMergeName')
  inputMergeName: ElementRef | undefined;
  mergeName = '';
  viewDropClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_NORMAL;
  viewBasketClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_NORMAL;
  showDropArea = false;
  allowedTypes: string[] = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  uploadableFileExtensions: string[] = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx'];

  entity: IEntity | INamedEntity;

  private mergeInProgress = false;
  private workflowSource = 'MERGE_BASKET';

  private static REMOVE_ACTION = {
    icon: 'highlight_off',
    title: 'Remove from Merge Basket',
    meta: 'action-remove-from-merge-basket',
  };
  private static SAVE_TO_ENTITY_ACTION = {
    icon: 'file_upload',
    title: 'Upload file to current record',
    meta: 'action-save-file-to-entity',
  };

  private static MERGED_BASKET_VIEW_CLASS_NORMAL = 'mbv-container';
  private static MERGED_BASKET_VIEW_CLASS_DROPOVER = 'mbv-container-dropOver';
  private static MERGED_BASKET_VIEW_CLASS_DROPOVER_BLUR = 'mbv-container-dropOver-blur';

  private static MAX_BASKET_SIZE_MB = 20;
  private static MAX_BASKET_SIZE_BYTES = 1024 * 1024 * MergeBasketViewComponent.MAX_BASKET_SIZE_MB;
  private static MAX_LOCAL_FILE_SIZE_MB = 10; // should match limit of server
  private static MAX_LOCAL_FILE_SIZE_BYTES = 1024 * 1024 * MergeBasketViewComponent.MAX_LOCAL_FILE_SIZE_MB;

  constructor(
    private entityControllerService: EntityControllerService,
    private fileListController: FileListControllerService,
    private mergeControllerService: MergeControllerService,
    private messageNotificationService: MessageNotificationService,
    private localfileUploadService: LocalfileUploadService,
    private dialog: MatDialog,
    private ngZone: NgZone,
  ) {
    mergeControllerService.addToBasket$.subscribe((file) => this.addToBasket(file));
    mergeControllerService.getMergeFilesRequest$.subscribe((responseSubject) => this.getMergeFilesRequest(responseSubject));
    currentUser.currentUser$.subscribe((userInfo) => (this.currentUserId = userInfo?.id));
    entityControllerService.entitySelected$.subscribe((entity) => this.entitySelected(entity));
  }

  get mergeValid(): boolean {
    return (
      this.mergeName.length > 0 &&
      this.files.length > 1 &&
      this.entity !== undefined &&
      this.totalFileSizeBytes < MergeBasketViewComponent.MAX_BASKET_SIZE_BYTES
    );
  }

  get entityName(): string {
    return MergeBasketViewComponent.isNamedEntity(this.entity) ? this.entity.name : '';
  }

  get showSyncIcon(): boolean {
    return !MergeBasketViewComponent.isSameEntity(this.entity, this.entityControllerService.entity);
  }

  mergeNameInput(e: any): void {
    this.mergeName = e.target.value.trim();
  }

  syncEntity(): void {
    this.entitySelected(this.entityControllerService.entity);
  }

  clearBasket(): void {
    const copy = [...this.files];
    copy.forEach((f) => this.removeFileFromBasket(f.data));
  }

  itemsReordered(items: ISimpleTableRow[]): void {
    console.log('itemsReordered');
    // this will trigger the simple-table items setter
    this.files = items;
  }

  itemActionClick(itemAction: { item: IMergeFile | ILocalMergeFile; action: string }): void {
    console.log('item ACTION click in merge basket view', itemAction);
    if (itemAction.action === 'action-remove-from-merge-basket') {
      const srcFile = itemAction.item;
      this.removeFileFromBasket(srcFile);
    } else if (itemAction.action === 'action-save-file-to-entity') {
      this.messageNotificationService.info('File has been uploaded.', `${itemAction.item.name} has been uploaded to your record.`);
      const srcFile = itemAction.item as ILocalMergeFile;
      this.saveFileToEntity(srcFile);
    }
  }

  itemRowDoubleClick(item: IMergeFile): void {
    this.removeFileFromBasket(item);
  }

  uploadFile(event: any | DataTransfer): void {
    const files: FileList = event.files;
    const rejectedFiles = [];

    for (let i = 0; i < files.length; i++) {
      const file: File = files[i];

      const typeOk = this.allowedTypes.indexOf(file.type) !== -1;
      const sizeOk = file.size < MergeBasketViewComponent.MAX_LOCAL_FILE_SIZE_BYTES;

      if (typeOk && sizeOk) {
        this.addUploadedFilesToBasket(file);
        continue;
      }

      if (!typeOk) {
        rejectedFiles.push(`'${file.name}' is not a supported type.`);
      }

      if (!sizeOk) {
        rejectedFiles.push(`'${file.name}' exceeds the maximum size of ${MergeBasketViewComponent.MAX_LOCAL_FILE_SIZE_MB}MB`);
      }
    }

    if (rejectedFiles.length > 0) {
      this.messageNotificationService.error('The following files were rejected.', rejectedFiles);
    }
  }

  mergeFiles(): void {
    this.config.preMergeChecker.check(this.entity, this.mergeName).subscribe((ok) => {
      if (!ok) {
        this.messageNotificationService.info(
          'Merge file name already exists',
          `'${this.mergeName}' already exists, please use a different file name.`,
        );
        return;
      }

      // TODO: Refactor - a separate function? Service/Component?
      const files = this.files.map((row) => row.data);

      this.config.preMergeChecker.checkFilesExist(files).subscribe((failedFiles) => {
        if (failedFiles.length > 0) {
          const isFailedFile = (id) => failedFiles.find((ff) => ff.id === id);
          this.files = this.files.filter((row) => !isFailedFile(row.data.id));
          let failedFilesList = failedFiles.map((file) => file.name).join(', ');
          failedFilesList = '<span class="red">' + failedFilesList + '</span>';

          let title;
          let message;
          let cancelButtonText = 'Cancel';
          const confirmButtonText = 'Continue';
          let hideConfirmButton = false;

          if (this.files.length > 1) {
            title = 'Some files no longer exist in <span class="capitalise">' + this.config.namespace + '</span>';
            message =
              failedFiles.length > 1
                ? failedFilesList + ' have been removed since adding to the Merge basket. <br> Would you like to continue without them?'
                : failedFilesList + ' has been removed since adding to the Merge basket. <br> Would you like to continue without it?';
          } else {
            title = this.files.length === 0 ? 'No files remain in the Merge basket' : 'Only 1 file remains in the Merge basket';
            message = 'You will need 2 or more files in the basket to merge';
            hideConfirmButton = true;
            cancelButtonText = 'OK';
          }

          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: this.renderMode === 'web-app' ? '55vw' : '85vw',
            height: 'auto',
            autoFocus: true,
            panelClass: runtimeEnvironment.origin.namespace + '-namespace',
            data: {
              titleIcon: 'merge_type',
              title: title,
              messageIcon: 'description',
              message: message,
              cancelButtonText: cancelButtonText,
              confirmButtonText: confirmButtonText,
              hideConfirmButton: hideConfirmButton,
            },
          });

          dialogRef.afterClosed().subscribe((data) => {
            if (!data) {
              return;
            }

            this.mergeFilesImpl();
          });
        } else {
          this.mergeFilesImpl();
        }
      });
    });
  }

  fileMergeUpdate(entity: IEntity, status: string): void {
    console.log('fileMerge status update', status, entity.id, status);
    this.mergeControllerService.mergeStatus(MergeStatus.UPDATE, entity, status);
  }

  fileMergeComplete(entity: IEntity): void {
    console.log('fileMerge complete for entity', entity);

    // the order here is important - the basket must clear its contents before the file list view updates
    // otherwise the file list view would potentially remove the files from its internal list
    this.files = [];
    this.fileListController.refresh(entity);
    this.mergeControllerService.mergeStatus(MergeStatus.COMPLETE, entity, 'MergeComplete');
    this.mergeInProgress = false;

    // Refresh Invenias' Documents list
    if (this.renderMode === 'chrome-extension') {
      chrome.runtime.sendMessage({ message: 'refresh_docs' });
    }

    this.syncEntity();
  }

  fileMergeError(entity: IEntity, message: String): void {
    this.mergeInProgress = false;
    this.mergeControllerService.mergeStatus(MergeStatus.ERROR, entity);
    const owner = MergeBasketViewComponent.isNamedEntity(entity) ? entity.name : 'record';
    this.messageNotificationService.error(`Merge failed for ${owner}`, message);
  }

  onDragOver(event) {
    this.showDropArea = true;
    this.viewDropClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_DROPOVER;
    this.viewBasketClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_DROPOVER_BLUR;
    event.stopPropagation();
    event.preventDefault();
  }

  onDrop(event) {
    event.stopPropagation();
    event.preventDefault();

    this.uploadFile(event.dataTransfer);

    this.showDropArea = false;
    this.viewBasketClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_NORMAL;
  }

  onDragEnd(event) {
    this.showDropArea = false;
    this.viewBasketClass = MergeBasketViewComponent.MERGED_BASKET_VIEW_CLASS_NORMAL;
    event.stopPropagation();
    event.preventDefault();
  }

  private addUploadedFilesToBasket(file: File): void {
    const uploadedFile: ILocalMergeFile = {
      id: undefined,
      name: file.name,
      type: MergeBasketViewComponent.LOCAL_FILE,
      belongsTo: {
        id: currentUser.id,
        type: this.config.entityType,
        name: currentUser.name,
      },
      size: file.size,
      file: file,
    };

    const actions = [MergeBasketViewComponent.REMOVE_ACTION];
    if (this.entityControllerService.entity) {
      actions.push(MergeBasketViewComponent.SAVE_TO_ENTITY_ACTION);
    }

    const cells = [{ text: uploadedFile.name }, { text: 'Local File' }, { actions: actions }];
    const row = { cells: cells, data: uploadedFile };
    this.files = [...this.files, row]; // trigger simple-table setter.
    this.totalFileSizeBytes += file.size ? file.size : 0;
  }

  private addToBasket(file: IMergeFile): void {
    const owner = MergeBasketViewComponent.isNamedEntity(file.belongsTo) ? file.belongsTo?.name : '';
    const cells = [{ text: file.name }, { text: owner }, { actions: [MergeBasketViewComponent.REMOVE_ACTION] }];
    const row = { cells: cells, data: file };
    this.files = [...this.files, row];
    this.totalFileSizeBytes += file.size ? file.size : 0;
  }

  private removeFileFromBasket(srcFile: IMergeFile): void {
    console.log('removing file from basket');

    this.mergeControllerService.removeFromBasket(srcFile);
    const index = this.files.findIndex((f) => f.data === srcFile);
    const files = [...this.files];

    if (files.length > 1) {
      this.files = files.filter((s, i) => i !== index);
    } else {
      // this is daft but does trigger change detection
      this.files = [{ cells: [] } as ISimpleTableRow];
      setTimeout(() => (this.files = new Array(0)), 0);
    }

    this.totalFileSizeBytes -= srcFile.size ? srcFile.size : 0;
  }

  private saveFileToEntity(srcFile: ILocalMergeFile): void {
    this.mergeControllerService.saveFileToEntity(srcFile).subscribe((newMergeFile) => {
      const index = this.files.findIndex((f) => f.data === srcFile);
      const owner = MergeBasketViewComponent.isNamedEntity(newMergeFile.belongsTo) ? newMergeFile.belongsTo?.name : '';
      const cells = [{ text: newMergeFile.name }, { text: owner }, { actions: [MergeBasketViewComponent.REMOVE_ACTION] }];
      this.files[index] = { cells: cells, data: newMergeFile };
    });
  }

  private mergeFilesImpl(): void {
    console.log('Start merge process', this.files);
    this.mergeInProgress = true;

    this.mergeControllerService.mergeStatus(MergeStatus.STARTED, this.entity, 'MergeStarted');

    const files = this.files.map((row) => row.data);

    const messageId = this.messageNotificationService.info('Uploading files for merge. Please wait.');
    this.localfileUploadService
      .uploadMergeFiles(files, runtimeEnvironment.origin.namespace)
      .pipe(tap((_) => this.messageNotificationService.hide(messageId)))
      .subscribe(
        (_) => {
          const mergeFilesTask = new MergeFilesTask(
            this.config.namespace,
            this.mergeName,
            this.entity,
            files,
            this.config.fileToPathConverter,
            this,
            this.dialog,
            this.messageNotificationService,
            this.workflowSource,
          );
          const taskRunner = new WorkflowTaskRunner(this.config.workflowService, mergeFilesTask);
          taskRunner.run().subscribe(
            // tslint:disable-next-line:no-shadowed-variable
            (_) => {},
            (error) => {
              this.mergeControllerService.mergeStatus(MergeStatus.ERROR, this.entity);
              this.mergeInProgress = true;
              this.messageNotificationService.error('Merge failed.', error);
            },
          );
        },
        (error) => {
          this.mergeControllerService.mergeStatus(MergeStatus.ERROR, this.entity);
          this.mergeInProgress = true;
          this.messageNotificationService.error(
            'Merge failed. Failed to upload all files.',
            error,
            undefined,
            ErrorSeverity.CRITICAL,
          );
        },
      );
  }

  private entitySelected(entity: IEntity): void {
    // don't change entities during a merge because if there is an error during merge we want to deal with it on the original (merge) entity
    if (this.mergeInProgress) {
      return;
    }

    this.ngZone.run(() => {
      this.entity = entity;
    });

    const uploadedFiles = this.files.filter((f) => !f.data.id); // uploaded files do not have an id
    if (uploadedFiles.length === 0) {
      return;
    }

    const actions = [MergeBasketViewComponent.REMOVE_ACTION];
    if (this.entityControllerService.entity) {
      actions.push(MergeBasketViewComponent.SAVE_TO_ENTITY_ACTION);
    }

    uploadedFiles.forEach((f) => {
      const actionCell = f.cells.find((c) => c.actions);
      actionCell.actions = actions;
    });

    // force UI to update the actions on the uploaded files
    this.ngZone.run(() => {});
  }

  private getMergeFilesRequest(reply: AsyncSubject<IMergeFile[]>): void {
    const mergeFiles = this.files.filter((row) => !row.data.file && !row.data.s3).map((row) => row.data);
    reply.next(mergeFiles);
    reply.complete();
  }

  private static isNamedEntity(e: IEntity | INamedEntity): e is INamedEntity {
    return e && 'name' in e;
  }

  private static isSameEntity(a: IEntity | undefined, b: IEntity | undefined): boolean {
    return a?.id === b?.id && a?.type === b?.type;
  }

  showTooltip(): void {
    this.tooltipTimer = setTimeout(() => {
      this.tooltipVisible = true;
    }, 500);
  }

  hideTooltip(): void {
    clearTimeout(this.tooltipTimer);
    this.tooltipVisible = false;
  }

  get mergeTooltip(): string {
    if (this.mergeValid) {
      return '';
    }

    const reasons: string[] = [];

    if (this.files.length < 2) {
      reasons.push('• At least 2 files are needed to merge.');
    }

    if (!this.mergeName || this.mergeName.trim().length === 0) {
      reasons.push('• Enter a file name for the merged file.');
    }

    if (!this.entity) {
      reasons.push('• Navigate to an entity/record for the file to be merged to.');
    }

    return reasons.join('\n');
  }
}
